import posthog from "posthog-js";
import type { PostHogConfig } from "posthog-js";
import { env, isAnalyticsEnabled } from "./env";

/**
 * Initialize PostHog analytics with environment-specific configuration
 */
export function initAnalytics(): void {
  if (!isAnalyticsEnabled()) {
    return;
  }

  try {
    const config: Partial<PostHogConfig> = {
      api_host: env.posthog.apiHost,
      autocapture: env.posthog.autocapture,
      capture_pageview: env.posthog.capturePageview,
      capture_pageleave: env.posthog.capturePageleave,
      session_recording: env.posthog.sessionRecording,
      __add_tracing_headers: [new URL(env.apiUrl).hostname],
    };

    posthog.init(env.posthog.apiKey, config);
  } catch (error) {
    console.error("[Analytics] Failed to initialize PostHog:", error);
  }
}

/**
 * Identify user in PostHog when they sign in
 */
export function identifyUser(
  userId: string,
  userProperties?: Record<string, unknown>,
): void {
  if (!isAnalyticsEnabled()) return;

  try {
    posthog.identify(userId, {
      ...userProperties,
      environment: env.environment,
    });
  } catch (error) {
    console.error("[Analytics] Failed to identify user:", error);
  }
}

/**
 * Reset PostHog when user signs out
 */
export function resetAnalytics(): void {
  if (!isAnalyticsEnabled()) return;

  try {
    posthog.reset();
  } catch (error) {
    console.error("[Analytics] Failed to reset PostHog:", error);
  }
}

/**
 * Track custom events
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (!isAnalyticsEnabled()) return;

  try {
    posthog.capture(eventName, {
      ...properties,
      environment: env.environment,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[Analytics] Failed to track event:", error);
  }
}

/**
 * Set user properties
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  if (!isAnalyticsEnabled()) return;

  try {
    posthog.people.set(properties);
  } catch (error) {
    console.error("[Analytics] Failed to set user properties:", error);
  }
}

/**
 * Track page view manually (useful for SPAs)
 */
export function trackPageView(path?: string): void {
  if (!isAnalyticsEnabled()) return;

  try {
    posthog.capture("$pageview", {
      $current_url: path || window.location.href,
    });
  } catch (error) {
    console.error("[Analytics] Failed to track page view:", error);
  }
}

/**
 * Capture error event with stack trace
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>,
): void {
  if (!isAnalyticsEnabled()) return;

  try {
    posthog.capture("$exception", {
      $exception_type: error.name,
      $exception_message: error.message,
      $exception_stack_trace_raw: error.stack,
      ...context,
      environment: env.environment,
      timestamp: new Date().toISOString(),
    });
  } catch (captureError) {
    console.error("[Analytics] Failed to capture error:", captureError);
  }
}

// Export posthog instance for advanced use cases
export { posthog };
